// src/admin/ItemAddModal.js
import React, { useState, useEffect } from 'react';
import { addItem } from '../api'; // Adjust path as needed

// --- Material UI Imports ---
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'; // Use v2/v5 adapter path
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Box from '@mui/material/Box'; // Added for color swatch
import Typography from '@mui/material/Typography'; // Added for color label

const initialFormData = {
    assetid: '',
    classid: '',
    instanceid: '',
    name: '',
    name_color: 'F0F0F0', // Default color from the new options
    icon_url: '',
    accountId: '',
    tradable: true,
    tradableDate: null,
    wear: '',
    excludedFromSpin: false,
    waitingForTrade: false,
    itemTag: 'Staging', // Default tag
};

// --- NEW: Color options for the selector ---
const colorOptions = [
    { value: 'F0F0F0', label: 'Default (Whiteish)' },
    { value: '809FEB', label: 'Light Blue' },
    { value: '3361FD', label: 'Blue' },
    { value: 'A947F5', label: 'Purple' },
    { value: 'FF1EE9', label: 'Pink' },
    { value: 'FF3F3F', label: 'Red' },
    { value: 'FFE819', label: 'Yellow' },
    { value: 'FF8430', label: 'Orange' },
];

function ItemAddModal({ open, onClose, onAddSuccess }) {
    const [formData, setFormData] = useState(initialFormData);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        // Reset form when modal opens
        if (open) {
            setFormData(initialFormData);
            setSaveError('');
        }
    }, [open]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleDateChange = (newValue) => {
        setFormData(prev => ({
            ...prev,
            tradableDate: newValue,
        }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError('');

        // Basic Validation
        if (!formData.assetid || !formData.classid || !formData.instanceid || !formData.name || !formData.icon_url || !formData.accountId || !formData.name_color) {
            setSaveError('Please fill in all required fields (Asset ID, Class ID, Instance ID, Name, Icon URL, Account ID, Name Color).');
            setIsSaving(false);
            return;
        }

        try {
            const payload = {
                ...formData,
                assetid: parseInt(formData.assetid, 10),
                classid: parseInt(formData.classid, 10),
                instanceid: parseInt(formData.instanceid, 10),
                // Ensure tradableDate is ISO string or null
                tradableDate: formData.tradableDate ? formData.tradableDate.toISOString() : null,
                // Ensure name_color doesn't have '#'
                name_color: formData.name_color.replace('#', ''),
                // Ensure optional booleans are sent
                excludedFromSpin: formData.excludedFromSpin || false,
                waitingForTrade: formData.waitingForTrade || false,
                // Ensure itemTag is sent, default is already 'Staging'
                itemTag: formData.itemTag || 'Staging',
            };

            await addItem(payload);
            onAddSuccess(); // Callback to refresh list in parent
            onClose(); // Close modal on success
        } catch (err) {
            const errMsg = err.response?.data?.title
                || err.response?.data?.message
                || err.message
                || 'Failed to add item.';
            setSaveError(errMsg);
            console.error("Add item error:", err.response || err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Add New Item</DialogTitle>
            <DialogContent>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {saveError && <Alert severity="error">{saveError}</Alert>}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField required label="Asset ID" name="assetid" type="number" value={formData.assetid} onChange={handleChange} fullWidth size="small" disabled={isSaving} />
                            <TextField required label="Class ID" name="classid" type="number" value={formData.classid} onChange={handleChange} fullWidth size="small" disabled={isSaving} />
                            <TextField required label="Instance ID" name="instanceid" type="number" value={formData.instanceid} onChange={handleChange} fullWidth size="small" disabled={isSaving} />
                        </Stack>
                        <TextField required label="Name" name="name" value={formData.name} onChange={handleChange} fullWidth size="small" disabled={isSaving} />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {/* --- MODIFIED: Name Color Selector --- */}
                            <FormControl fullWidth size="small" required disabled={isSaving}>
                                <InputLabel id="name-color-label">Name Color</InputLabel>
                                <Select
                                    labelId="name-color-label"
                                    label="Name Color"
                                    name="name_color"
                                    value={formData.name_color}
                                    onChange={handleChange}
                                >
                                    {colorOptions.map((color) => (
                                        <MenuItem key={color.value} value={color.value}>
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <Box sx={{ width: 20, height: 20, backgroundColor: `#${color.value}`, border: '1px solid grey', borderRadius: '4px' }} />
                                                <Typography variant="body2">{color.label} (#{color.value})</Typography>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {/* --- END MODIFICATION --- */}
                            <TextField label="Wear (e.g., FN or 0.0123)" name="wear" value={formData.wear} onChange={handleChange} fullWidth size="small" disabled={isSaving} />
                        </Stack>
                        <TextField required label="Icon URL" name="icon_url" value={formData.icon_url} onChange={handleChange} fullWidth size="small" placeholder="Steam Economy Image URL or path" disabled={isSaving} />
                        <TextField required label="Account ID (Bot SteamID64)" name="accountId" value={formData.accountId} onChange={handleChange} fullWidth size="small" disabled={isSaving} inputProps={{ maxLength: 17 }} />
                        <Stack direction="row" spacing={2} alignItems="center">
                            <FormControlLabel control={<Checkbox name="tradable" checked={formData.tradable} onChange={handleChange} disabled={isSaving} />} label="Tradable" />
                            <DateTimePicker
                                label="Tradable After (UTC)"
                                value={formData.tradableDate}
                                onChange={handleDateChange}
                                disabled={isSaving || formData.tradable} // Disable if marked as tradable
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                ampm={false} // Use 24-hour format
                            />
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <FormControlLabel control={<Checkbox name="excludedFromSpin" checked={formData.excludedFromSpin} onChange={handleChange} disabled={isSaving} />} label="Exclude from Spin" />
                            <FormControlLabel control={<Checkbox name="waitingForTrade" checked={formData.waitingForTrade} onChange={handleChange} disabled={isSaving} />} label="Waiting for Trade" />
                        </Stack>
                        <FormControl fullWidth size="small" disabled={isSaving}>
                            <InputLabel id="item-tag-label">Item Tag</InputLabel>
                            <Select
                                labelId="item-tag-label"
                                label="Item Tag"
                                name="itemTag"
                                value={formData.itemTag}
                                onChange={handleChange}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                <MenuItem value="Staging">Staging</MenuItem>
                                <MenuItem value="Featured">Featured</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </LocalizationProvider>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={isSaving}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSaving ? 'Adding...' : 'Add Item'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ItemAddModal;
